import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import { MdUpload } from '@teamworksdev/icons';

import { PageHeader } from '../../../components/layout/page-header';
import { useWorkoutImport } from './useWorkoutImport';
import { Org } from '../exercise-library-management/types';
import { ImportResultsView } from './ImportResultsView';
import { OrgScopedMetricsProvider } from './OrgScopedMetricsContext';

interface ToolbarProps {
  orgs: Org[] | undefined;
  isLoading: boolean;
  selectedOrgId: string | null;
  onOrgChange: (orgId: string | null) => void;
}

function Toolbar({
  orgs,
  isLoading,
  selectedOrgId,
  onOrgChange,
}: ToolbarProps) {
  return (
    <Box className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <Autocomplete
        options={orgs || []}
        getOptionLabel={(option: Org) => option.name}
        value={orgs?.find((org) => org.id === selectedOrgId) || null}
        onChange={(_, newValue) => onOrgChange(newValue?.id || null)}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            label="Organization"
            placeholder="Select an organization"
          />
        )}
        loading={isLoading}
        className="w-full sm:w-72"
      />
    </Box>
  );
}

export const WorkoutImportPage = () => {
  const {
    selectedOrgId,
    file,
    fileInputRef,
    isProcessing,
    validationResult,
    template,
    uploadId,
    templateName,
    isSaving,
    canSave,
    orgsQuery,
    handleOrgChange,
    handleFileSelected,
    handleTemplateNameChange,
    handleLayersChange,
    handleApproveSuggestedMetric,
    handleRemoveSuggestedMetric,
    pendingSuggestedMetricIds,
    pendingMissingExerciseIds,
    addingExerciseIds,
    handleAddMissingExercise,
    handleRemoveMissingExercise,
    handleSave,
  } = useWorkoutImport();

  return (
    <div className="flex flex-col h-full overflow-auto">
      <PageHeader title="Workout Import" />
      <div className="flex flex-col gap-6 p-6">
        <Toolbar
          orgs={orgsQuery.data}
          isLoading={orgsQuery.isLoading}
          selectedOrgId={selectedOrgId}
          onOrgChange={handleOrgChange}
        />

        {/* Step 2: file upload — disabled until an org is selected (per spec). */}
        <Paper className="p-6">
          <Box className="flex items-center gap-4 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileSelected}
              id="workout-import-file"
              className="sr-only"
              disabled={!selectedOrgId || isProcessing}
            />
            <label htmlFor="workout-import-file">
              <Button
                variant="contained"
                component="span"
                startIcon={
                  isProcessing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <MdUpload />
                  )
                }
                disabled={!selectedOrgId || isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Choose File'}
              </Button>
            </label>
            {file && !isProcessing && (
              <Typography variant="body2" className="text-gray-700">
                {file.name}
              </Typography>
            )}
          </Box>

          {!selectedOrgId && (
            <Typography variant="body2" className="text-gray-500 mt-3">
              Select an organization to enable file upload.
            </Typography>
          )}
        </Paper>

        {/* Step 3: results — renders the parsed template in the embedded
            WorkoutTemplateSheet editor (read-only) with AI-suggested matches
            pre-filled. Wrapped in OrgScopedMetricsProvider so the sheet's
            useMetrics / useBenchmarks read the selected org's metrics + grids
            via admin-scoped endpoints (admin sessions can't call the
            orgProcedure / teamProcedure versions). */}
        {validationResult && selectedOrgId && (
          <OrgScopedMetricsProvider orgId={selectedOrgId}>
            <ImportResultsView
              result={validationResult}
              template={template}
              uploadId={uploadId}
              templateName={templateName}
              onTemplateNameChange={handleTemplateNameChange}
              onLayersChange={handleLayersChange}
              pendingSuggestedMetricIds={pendingSuggestedMetricIds}
              onApproveSuggestedMetric={handleApproveSuggestedMetric}
              onRemoveSuggestedMetric={handleRemoveSuggestedMetric}
              pendingMissingExerciseIds={pendingMissingExerciseIds}
              addingExerciseIds={Array.from(addingExerciseIds)}
              onAddMissingExercise={handleAddMissingExercise}
              onRemoveMissingExercise={handleRemoveMissingExercise}
              canSave={canSave}
              onSave={handleSave}
              isSaving={isSaving}
            />
          </OrgScopedMetricsProvider>
        )}
      </div>
    </div>
  );
};
